import { Task, Timestamp } from '@griply/types';
import {
  createDailyEventsFromExternalEvents,
  getWeekStartsOn,
} from '@griply/utils';
import { endOfDay, startOfDay } from 'date-fns';
import {
  mapExternalEventToFullCalendarEvent,
  useTodayExternalCalendarEvents,
} from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import {
  getGoalsDeadlineEvents,
  getTasksEventsForDateRange,
} from 'shared/lib/full-calendar';

export const useDayEvents = (day: Timestamp, tasks: Task[]) => {
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const goals = useGoals();
  const today = useToday();
  const externalEvents = useTodayExternalCalendarEvents();

  const taskEvents = useMemo(
    () =>
      getTasksEventsForDateRange(
        user?.settings?.calendar?.hideHabits
          ? tasks.filter(({ schedules }) => !schedules?.length)
          : tasks,
        {
          startDate: startOfDay(day),
          endDate: endOfDay(day),
          today,
          weekStartsOn,
          hideCompleted: user?.settings?.calendar?.hideCompletedTasks,
        },
      ),
    [
      user?.settings?.calendar?.hideHabits,
      user?.settings?.calendar?.hideCompletedTasks,
      tasks,
      day,
      today,
      weekStartsOn,
    ],
  );

  const goalEvents = useMemo(() => getGoalsDeadlineEvents(goals), [goals]);

  const externalFullCalendarEvents = useMemo(
    () =>
      createDailyEventsFromExternalEvents(externalEvents).map((externalEvent) =>
        mapExternalEventToFullCalendarEvent(externalEvent),
      ),
    [externalEvents],
  );

  const events = useMemo(
    () => [
      ...(user?.settings?.calendar?.hideExternalCalendarEvents
        ? []
        : externalFullCalendarEvents),
      ...taskEvents,
      ...goalEvents,
    ],
    [
      externalFullCalendarEvents,
      goalEvents,
      taskEvents,
      user?.settings?.calendar?.hideExternalCalendarEvents,
    ],
  );

  return {
    events,
    tasks,
  };
};
